import { useState, useRef, useCallback } from 'react';

export interface UseBulkTableActionsOptions<T extends { id: string }> {
  /** Current items in the table (used for totalCount and to derive selected items). */
  items: T[];
  /** Called when user confirms bulk delete in the modal. Perform CRUD here. */
  onBulkDeleteConfirm: (ids: string[]) => void;
}

export interface BulkTableActionsTableProps {
  onBulkDelete: (ids: string[]) => void;
  onRegisterToggleAll: (fn: () => void) => void;
  onRegisterBulkDelete: (fn: () => void) => void;
  onRegisterClearSelection: (fn: () => void) => void;
  onSelectedCountChange: (count: number) => void;
  onIsAllSelectedChange: (isAll: boolean) => void;
  onSelectedItemsChange: <T>(items: T[]) => void;
}

export interface BulkTableActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pendingCount: number;
}

export interface UseBulkTableActionsReturn<T extends { id: string }> {
  selectedCount: number;
  selectedItems: T[];
  /** Pass these to your table (LeadTable, PaymentTable, etc.). */
  tableProps: BulkTableActionsTableProps;
  /** Pass to BulkActionsBar: selectedCount, totalCount, selectedItems, onDeleteSelected. Merge with statusOptions + onUpdateStatus in the page. */
  bulkBarProps: {
    selectedCount: number;
    totalCount: number;
    selectedItems: T[];
    onDeleteSelected: () => void;
    onClearSelection: () => void;
  };
  /** Props for the bulk-delete confirmation modal. */
  bulkDeleteModal: BulkTableActionsModalProps;
}

/**
 * Centralizes bulk selection + floating bar + delete confirmation modal for any table.
 * Use with tables that support useTableSelection (LeadTable, PaymentTable, etc.).
 *
 * To add bulk actions to a new table:
 * 1. Call useBulkTableActions({ items: yourFilteredItems, onBulkDeleteConfirm: (ids) => yourCRUD.bulkRemove(ids) }).
 * 2. Render <BulkActionsBar {...bulkBarProps} statusOptions={...} onUpdateStatus={...} /> (statusOptions/onUpdateStatus optional).
 * 3. Pass tableProps to your table; if the table uses a specific callback name (e.g. onSelectedLeadsChange), map tableProps.onSelectedItemsChange to it.
 * 4. Render <ConfirmModal {...bulkDeleteModal} message={t('...confirmBulkDelete', { count: bulkDeleteModal.pendingCount })} />.
 */
export function useBulkTableActions<T extends { id: string }>({
  items,
  onBulkDeleteConfirm,
}: UseBulkTableActionsOptions<T>): UseBulkTableActionsReturn<T> {
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [pendingBulkDelete, setPendingBulkDelete] = useState<string[]>([]);

  const bulkDeleteRef = useRef<() => void>(() => {});
  const toggleAllRef = useRef<() => void>(() => {});
  const clearSelectionRef = useRef<() => void>(() => {});
  const isAllSelectedRef = useRef(false);

  const handleBulkDelete = useCallback((ids: string[]) => {
    setPendingBulkDelete(ids);
    setIsBulkDeleteModalOpen(true);
  }, []);

  const confirmBulkDelete = useCallback(() => {
    const ids = pendingBulkDelete;
    if (ids.length === 0) return;
    onBulkDeleteConfirm(ids);
    setPendingBulkDelete([]);
    setIsBulkDeleteModalOpen(false);
    clearSelectionRef.current();
  }, [pendingBulkDelete, onBulkDeleteConfirm]);

  const closeModal = useCallback(() => {
    setIsBulkDeleteModalOpen(false);
    setPendingBulkDelete([]);
  }, []);

  const tableProps: BulkTableActionsTableProps = {
    onBulkDelete: handleBulkDelete,
    onRegisterToggleAll: (fn) => { toggleAllRef.current = fn; },
    onRegisterBulkDelete: (fn) => { bulkDeleteRef.current = fn; },
    onRegisterClearSelection: (fn) => { clearSelectionRef.current = fn; },
    onSelectedCountChange: setSelectedCount,
    onIsAllSelectedChange: (isAll) => { isAllSelectedRef.current = isAll; },
    onSelectedItemsChange: setSelectedItems as (items: unknown[]) => void,
  };

  return {
    selectedCount,
    selectedItems,
    tableProps,
    bulkBarProps: {
      selectedCount,
      totalCount: items.length,
      selectedItems,
      onDeleteSelected: () => bulkDeleteRef.current(),
      onClearSelection: () => clearSelectionRef.current(),
    },
    bulkDeleteModal: {
      isOpen: isBulkDeleteModalOpen,
      onClose: closeModal,
      onConfirm: confirmBulkDelete,
      pendingCount: pendingBulkDelete.length,
    },
  };
}
